/**
 * PactsPage — minimal UI for the Study Pacts retention pillar.
 *
 * One column: invite a friend, accept incoming invites, show active +
 * completed pacts with mutual streak counts. Designed for mobile-first.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Plus, Loader2, Send, Flame, X, Sparkles, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api, { apiUrl } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import PageSkeleton from '../components/PageSkeleton';
import EmptyState from '../components/EmptyState';

export default function PactsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pacts, setPacts] = useState([]);
  const [invites, setInvites] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [draft, setDraft] = useState({ to_email: '', daily_minutes_target: 30, length_days: 14 });
  const [submitting, setSubmitting] = useState(false);
  const [invitingUserId, setInvitingUserId] = useState(null);
  const [lastInviteShareUrl, setLastInviteShareUrl] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [pactsRes, suggRes] = await Promise.all([
        api.get(apiUrl('/pacts')),
        api.get(apiUrl('/pacts/suggestions')).catch(() => ({ data: { suggestions: [] } })),
      ]);
      setPacts(pactsRes.data.pacts || []);
      setInvites(pactsRes.data.incoming_invites || []);
      setSuggestions(suggRes.data.suggestions || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Auto-handle ?invite={id} (from a forwarded OG share link). If that
  // invite is in our incoming_invites list, accept it inline and clear the
  // query param. If not, show a friendly toast — likely the invite was
  // not for this user (different email) or already accepted.
  useEffect(() => {
    const inviteId = searchParams.get('invite');
    if (!inviteId || loading || !user) return;
    const matched = invites.find((i) => i.id === inviteId);
    if (matched) {
      (async () => {
        try {
          await api.post(apiUrl(`/pacts/accept/${inviteId}`));
          toast.success('Pact accepted 💪');
          searchParams.delete('invite');
          setSearchParams(searchParams, { replace: true });
          await load();
        } catch { toast.error('Could not accept invite'); }
      })();
    } else {
      // No matching invite — show a hint without mutating URL on first paint
      toast.info('Invite not found in your inbox. It may have been accepted, expired, or sent to a different email.');
      searchParams.delete('invite');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, invites, user]);

  const inviteSuggested = async (suggestion) => {
    setInvitingUserId(suggestion.user.id);
    try {
      const { data } = await api.post(apiUrl('/pacts/invite'), {
        to_user_id: suggestion.user.id,
        daily_minutes_target: 30,
        length_days: 14,
      });
      // iter197 — surface which channels delivered (email always tried,
      // SMS only when target has phone + sms_enabled).
      const channels = [data?.delivery?.email && 'email', data?.delivery?.sms && 'SMS']
        .filter(Boolean).join(' + ');
      toast.success(`Invite sent to ${suggestion.user.name}${channels ? ` (via ${channels})` : ''} 💪`);
      await load();
    } catch { toast.error('Could not send invite'); }
    finally { setInvitingUserId(null); }
  };

  const sendInvite = async () => {
    if (!draft.to_email.includes('@')) return toast.error('Email required');
    setSubmitting(true);
    try {
      const { data } = await api.post(apiUrl('/pacts/invite'), draft);
      const inviteId = data?.invite?.id;
      const ogBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const shareUrl = inviteId ? `${ogBase}/api/og/pact/${inviteId}` : '';
      setLastInviteShareUrl(shareUrl);
      // iter197 — show delivery channels in toast so the inviter knows
      // whether SMS landed (only fires for registered users with phone).
      const channels = [data?.delivery?.email && 'email', data?.delivery?.sms && 'SMS']
        .filter(Boolean).join(' + ');
      const tail = channels ? ` · delivered via ${channels}` : '';
      toast.success(
        (data.target_is_user
          ? 'Invite sent — they\'ll see it on their pacts page'
          : 'Invite saved (your friend will see it after they sign up)') + tail
      );
      setDraft({ to_email: '', daily_minutes_target: 30, length_days: 14 });
      await load();
    } catch { toast.error('Could not send invite'); }
    finally { setSubmitting(false); }
  };

  const copyInviteLink = async () => {
    if (!lastInviteShareUrl) return;
    try {
      await navigator.clipboard.writeText(lastInviteShareUrl);
      toast.success('Invite link copied — paste it anywhere');
    } catch { toast.error('Copy failed'); }
  };

  const accept = async (id) => {
    try {
      await api.post(apiUrl(`/pacts/accept/${id}`));
      toast.success('Pact started 💪');
      await load();
    } catch { toast.error('Could not accept'); }
  };

  const checkin = async (pact) => {
    const minutes = parseInt(prompt(`How many minutes did you study today? (target: ${pact.daily_minutes_target})`), 10);
    if (!minutes || minutes < 0) return;
    try {
      const { data } = await api.post(apiUrl(`/pacts/${pact.id}/checkin`), { minutes });
      if (data.status === 'already_checked_in') toast.info(`Already checked in today (streak: ${data.streak})`);
      else if (data.target_met) toast.success(`Streak: ${data.streak} 🔥`);
      else toast.info(`Logged ${minutes} min — short of target, no streak boost`);
      await load();
    } catch { toast.error('Could not check in'); }
  };

  const cancel = async (id) => {
    if (!confirm('Leave this pact? Your partner will see it as broken.')) return;
    try {
      await api.delete(apiUrl(`/pacts/${id}`));
      await load();
      toast.success('Left pact');
    } catch { toast.error('Could not leave'); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="pacts-page">
      <div className="flex items-start justify-between mb-6 pb-4 border-b border-[var(--isp-glass-border)]">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[var(--isp-heading)] tracking-tight">Study Pacts</h1>
          <p className="text-sm text-[var(--isp-body)] mt-1">Two friends commit to a daily streak. Mutual accountability is 3× stickier than solo streaks.</p>
        </div>
        <Button onClick={() => setShowInvite(true)} className="bg-[#002FA7] hover:bg-[#00227A] text-white text-sm shrink-0" data-testid="pact-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New pact
        </Button>
      </div>

      {loading ? (
        <div data-testid="pacts-loading"><PageSkeleton variant="list" rows={4} /></div>
      ) : (
        <div className="space-y-4">
          {/* Zero-state — no pacts, no invites, no suggestions */}
          {pacts.length === 0 && invites.length === 0 && suggestions.length === 0 && (
            <EmptyState
              icon={Heart}
              title="Build a study pact"
              description="Daily commitments hit harder with a partner. Invite a friend (or use a suggestion above) and you'll keep each other accountable."
              actionLabel="Invite a friend"
              onAction={() => setShowInvite(true)}
              testId="pacts-empty-state"
            />
          )}
          {/* Incoming invites */}
          {invites.map((inv) => (
            <div key={inv.id} className="border border-[var(--isp-glass-border)] p-4 flex items-center justify-between gap-3" data-testid={`pact-invite-${inv.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[var(--isp-heading)]">Invite from a study buddy</p>
                <p className="text-xs text-[var(--isp-body)] mt-1">{inv.daily_minutes_target} min/day for {inv.length_days} days</p>
              </div>
              <Button onClick={() => accept(inv.id)} className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs h-8" data-testid={`pact-accept-${inv.id}`}>Accept</Button>
            </div>
          ))}

          {/* Study Buddy Match — algorithmic suggestions */}
          {suggestions.length > 0 && (
            <div className="border border-[var(--isp-glass-border)] bg-[var(--isp-glass)]" data-testid="pact-suggestions">
              <div className="px-4 py-3 border-b border-[var(--isp-glass-border)] flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#002FA7]" />
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--isp-muted)]">
                  Suggested study buddies
                </p>
              </div>
              <div>
                {suggestions.map((s) => (
                  <div key={s.user.id} className="px-4 py-3 flex items-center justify-between gap-3 border-b last:border-b-0 border-[var(--isp-glass-border)]" data-testid={`pact-suggestion-${s.user.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      {s.user.picture ? (
                        <img src={s.user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-[#002FA7] text-white text-sm font-bold flex items-center justify-center">
                          {s.user.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[var(--isp-heading)] truncate">{s.user.name}</p>
                        <p className="text-xs text-[var(--isp-muted)] truncate">
                          {s.shared_subjects.length > 0
                            ? `Both studying ${s.shared_subjects.slice(0, 2).join(', ')}`
                            : `${s.gap_similarity}% goal match`}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => inviteSuggested(s)}
                      disabled={invitingUserId === s.user.id}
                      size="sm"
                      className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs h-8 shrink-0"
                      data-testid={`pact-suggestion-invite-${s.user.id}`}
                    >
                      {invitingUserId === s.user.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Invite'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active + history pacts */}
          {pacts.length === 0 && invites.length === 0 && suggestions.length === 0 ? (
            <div className="border border-dashed border-[var(--isp-glass-border)] p-8 text-center">
              <Heart className="w-6 h-6 mx-auto text-[var(--isp-muted)] mb-3" strokeWidth={1.75} />
              <p className="text-sm text-[var(--isp-body)]">No pacts yet. Pair with a friend to make studying a shared ritual.</p>
            </div>
          ) : pacts.map((p) => {
            const isA = p.partner_a_id === user?.id;
            const myStreak = isA ? p.streak_a : p.streak_b;
            const partnerStreak = isA ? p.streak_b : p.streak_a;
            const active = p.status === 'active';
            return (
              <div key={p.id} className="border border-[var(--isp-glass-border)] p-4" data-testid={`pact-row-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[var(--isp-heading)]">
                      {p.daily_minutes_target} min/day · {p.length_days} days
                    </p>
                    <p className="text-xs text-[var(--isp-muted)] mt-0.5">
                      {p.started_at} → {p.ends_at} · <span className={active ? 'text-[#002FA7]' : 'text-red-500'}>{p.status}</span>
                    </p>
                    <div className="flex items-center gap-4 mt-3">
                      <div className="text-xs">
                        <span className="text-[var(--isp-muted)]">You</span>
                        <span className="ml-2 inline-flex items-center gap-1 font-bold text-[var(--isp-heading)]">
                          <Flame className="w-3 h-3 text-orange-500" /> {myStreak}
                        </span>
                      </div>
                      <div className="text-xs">
                        <span className="text-[var(--isp-muted)]">Partner</span>
                        <span className="ml-2 inline-flex items-center gap-1 font-bold text-[var(--isp-heading)]">
                          <Flame className="w-3 h-3 text-orange-500" /> {partnerStreak}
                        </span>
                      </div>
                    </div>
                  </div>
                  {active && (
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button onClick={() => checkin(p)} size="sm" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs h-8" data-testid={`pact-checkin-${p.id}`}>
                        Check in
                      </Button>
                      <button onClick={() => cancel(p.id)} className="text-[10px] text-[var(--isp-muted)] hover:text-red-500" data-testid={`pact-leave-${p.id}`}>Leave</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Invite modal */}
      <AnimatePresence>
        {showInvite && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ y: 20, scale: 0.95 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, scale: 0.95 }}
              className="w-full max-w-md bg-[var(--isp-page)] border border-[var(--isp-glass-border)] p-6"
              data-testid="pact-invite-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-[var(--isp-heading)]">Invite a study buddy</h2>
                <button onClick={() => { setShowInvite(false); setLastInviteShareUrl(''); }} className="text-[var(--isp-muted)] hover:text-[var(--isp-heading)]"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)]">Their email</label>
                  <Input type="email" value={draft.to_email} onChange={(e) => setDraft({ ...draft, to_email: e.target.value })} placeholder="friend@school.edu" className="mt-1 text-sm h-9" data-testid="pact-invite-email" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)]">Min/day</label>
                    <Input type="number" min={10} max={240} value={draft.daily_minutes_target} onChange={(e) => setDraft({ ...draft, daily_minutes_target: parseInt(e.target.value, 10) || 30 })} className="mt-1 text-sm h-9" data-testid="pact-invite-minutes" />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)]">Days</label>
                    <Input type="number" min={3} max={90} value={draft.length_days} onChange={(e) => setDraft({ ...draft, length_days: parseInt(e.target.value, 10) || 14 })} className="mt-1 text-sm h-9" data-testid="pact-invite-days" />
                  </div>
                </div>
                <Button onClick={sendInvite} disabled={submitting} className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white text-sm h-9" data-testid="pact-invite-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-2" /> Send invite</>}
                </Button>
                {lastInviteShareUrl && (
                  <div className="border border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)] p-3 mt-2" data-testid="pact-invite-share-link">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)] mb-2">
                      Or share this link directly
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={lastInviteShareUrl}
                        className="flex-1 text-xs font-mono px-2 py-1.5 bg-[var(--isp-page)] border border-[var(--isp-glass-border)] text-[var(--isp-body)] truncate"
                        data-testid="pact-invite-share-input"
                      />
                      <Button
                        onClick={copyInviteLink} size="sm"
                        className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs h-8 shrink-0"
                        data-testid="pact-invite-share-copy"
                      >
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                    </div>
                    <p className="text-[10px] text-[var(--isp-muted)] mt-2">
                      Pastes a rich preview card on WhatsApp, iMessage and Twitter.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
